import React, { useState, useRef, useCallback } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { InputCmp } from '../textbox/input'
import { FixedSizeList as List } from 'react-window'
import GradiantSkelton from '../gradiantSkelton/gradiantSkelton'
import { debounce } from '@/lib/utils'

export interface DropDownProps {
  hidden?: boolean
  fieldName: string
  defaultValue?: string
  placeholder?: string
  value: string
  readOnly?: boolean
  label?: string
  empty?: string
  dropdownValue: { label: string; value: string }[]
  childClass?: React.ComponentProps<'div'>['className']
  border?: boolean
  displayMap?: Record<string, string>
  labelClass?: string
  virtualized?: boolean
  itemHeight?: number
  maxHeight?: number
  onLoadMore?: (offset: number) => void
  hasMore?: boolean
  loading?: boolean
  searchable?: boolean
  onSearch?: (searchQuery: string) => void
  mobile?: boolean
}

export interface Props extends React.SelectHTMLAttributes<HTMLSelectElement> {
  node: DropDownProps
  handleChange: (key: string, value: string) => void
  smallresolution?: boolean
}

const DropDownCmp = ({ node, handleChange }: Props) => {
  const [value, setValue] = useState(node.value)
  const [searchTerm, setSearchTerm] = useState('')
  // const [isOpen, setIsOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    setValue(node?.value)
  }, [node?.value])

  // Auto-focus search input when dropdown opens
  // React.useEffect(() => {
  //   if (isOpen && node?.searchable && searchInputRef.current) {
  //     setTimeout(() => {
  //       searchInputRef.current?.focus()
  //     }, 100)
  //   }
  // }, [isOpen, node?.searchable])

  const handleDDChange = (field: string, value: string) => {
    handleChange(field, value)
    setValue(value)
  }

  const debouncedSearch = useCallback(
    debounce((...args: unknown[]) => {
      const searchQuery = args[0] as string
      if (node.onSearch) {
        node.onSearch(searchQuery)
      }
    }, 500),
    [node.onSearch],
  )

  const handleItemsRendered = React.useCallback(
    ({ visibleStopIndex }: { visibleStopIndex: number }) => {
      if (
        !node.virtualized ||
        !node.onLoadMore ||
        !node.hasMore ||
        node.loading
      )
        return

      if (visibleStopIndex >= (node.dropdownValue?.length || 0) - 5) {
        node.onLoadMore(node.dropdownValue?.length || 0)
      }
    },
    [
      node.virtualized,
      node.onLoadMore,
      node.hasMore,
      node.loading,
      node.dropdownValue?.length,
    ],
  )

  const VirtualizedItem = React.memo(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const element = node.dropdownValue?.[index]
      if (!element) return null

      return (
        <div style={style} data-testid={`dropdown-item-${element.value}`}>
          <SelectItem
            key={element.value}
            value={element.value}
            className='hover:bg-blue-500 transition-colors duration-200 cursor-pointer'
          >
            {element.label}
          </SelectItem>
        </div>
      )
    },
  )

  return (
    <div
      className='w-full  '
      style={node?.hidden ? { display: 'none' } : { display: 'block' }}
    >
      <Label
        htmlFor='email'
        className={`text-primary-foreground font-normal text-[12px] ${node?.labelClass}`}
      >
        {/* {smallresolution ? <GraphIcon /> : node.label} */}
        {node.label}
      </Label>
      {node?.readOnly ? (
        <InputCmp
          node={{
            id: '422',
            accName: 'Ticket information',
            formControl: 'textbox',
            fieldName: '',
            value: `${value}`,
            readOnly: true,
            required: false,
            disabled: false,
            layout: {
              lg: 'lg:w-1/2',
              md: 'md:w-1/2',
              sm: 'sm:w-1/2',
            },
            childClass: '',
          }}
          handleChange={() => {}}
        />
      ) : (
        <Select
          onValueChange={(value: string) =>
            handleDDChange(node.fieldName, value)
          }
          defaultValue={node?.defaultValue}
          name={node?.fieldName}
          value={value}
          // onOpenChange={setIsOpen}
        >
          <div className=''>
            <SelectTrigger
              className={cn(
                `rounded-radius-sm ${node?.mobile ? 'text-[12px]' : 'text-base'} text-primary-foreground ${node.border ? 'border border-primary-foreground' : ''}`,
                node?.childClass,
              )}
              style={{ color: 'var(--primary-foreground)' }}
            >
              <SelectValue
                data-testid={`${node.fieldName}-select-value`}
                className={`text-primary-foreground font-normal ${node?.mobile ? 'text-[12px]' : 'text-base'}`}
                placeholder={node?.placeholder}
              >
                {(() => {
                  const selectedOption = node.dropdownValue?.find(
                    element => element.value === value,
                  )
                  if (selectedOption) {
                    return (
                      node.displayMap?.[selectedOption.value] ||
                      selectedOption.label
                    )
                  } else if (node.placeholder) {
                    return node.placeholder
                  } else {
                    return node.label
                  }
                })()}
                {/* {(() => {
                  const selectedOption = node.dropdownValue?.find(
                    element => element.value === value,
                  )

                  if (selectedOption) {
                    return selectedOption.label
                  } else if (node.placeholder) {
                    return node.placeholder
                  } else {
                    return node.label
                  }
                })()} */}
              </SelectValue>
            </SelectTrigger>
          </div>
          <SelectContent
            className={
              ' text-primary-foreground bg-primary border border-border'
            }
          >
            {node.virtualized && node.dropdownValue ? (
              <>
                {node?.searchable && (
                  <div className='p-2'>
                    <input
                      data-testid={`search-input`}
                      ref={searchInputRef}
                      type='text'
                      value={searchTerm}
                      onChange={e => {
                        const newValue = e.target.value
                        setSearchTerm(newValue)
                        debouncedSearch(newValue)
                      }}
                      placeholder='Search...'
                      className='w-full border border-border rounded-md px-2 py-1 text-sm focus:outline-none text-primary-foreground bg-primary'
                    />
                  </div>
                )}
                {node.dropdownValue.length > 0 ? (
                  <div
                    className={
                      node.virtualized ? 'h-full' : 'h-full overflow-auto'
                    }
                  >
                    <List
                      data-testid='dropdown-list'
                      className='overflow-auto spec-scroll'
                      height={Math.min(
                        (node.dropdownValue?.length || 0) *
                          (node.itemHeight || 40),
                        node.maxHeight || 200,
                      )}
                      itemCount={
                        (node.dropdownValue?.length || 0) +
                        (node.hasMore ? 1 : 0)
                      }
                      itemSize={node.itemHeight || 40}
                      width='100%'
                      onItemsRendered={handleItemsRendered}
                    >
                      {({ index, style }) => {
                        if (index >= (node.dropdownValue?.length || 0)) {
                          return (
                            <div
                              style={style}
                              // className='flex items-center justify-center text-primary-foreground'
                            >
                              {/* {node.loading ? 'Loading...' : 'Load more'} */}
                              <GradiantSkelton
                                width='w-52'
                                height='h-8'
                                className='rounded-radius-sm mt-1'
                              />
                            </div>
                          )
                        }
                        return <VirtualizedItem index={index} style={style} />
                      }}
                    </List>
                  </div>
                ) : (
                  <div className='p-2 text-center text-sm text-gray-400'>
                    No records found
                  </div>
                )}
              </>
            ) : node?.dropdownValue && node.dropdownValue.length > 0 ? (
              node?.dropdownValue?.map((element, index) => (
                <SelectItem
                  key={index}
                  data-testid={`dropdown-item-${element.value}`}
                  value={element.value}
                  className='hover:bg-blue-500 transition-colors duration-200 cursor-pointer'
                >
                  {element.label}
                </SelectItem>
              ))
            ) : (
              <div className='p-2 text-center text-sm text-gray-400'>
                No data found
              </div>
            )}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}

export { DropDownCmp }
